import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Printer,
  Download,
  Loader2,
  Sparkles,
  User,
  Calendar,
  Stethoscope,
  AlertTriangle,
  ClipboardList,
} from "lucide-react";
import { getConsultation, getConsultationFullView } from "@/lib/adapters/consultations";

export default function AiConsultantReport() {
  const { caseId } = useParams();
  const [record, setRecord] = useState(null);
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const printRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      getConsultation(caseId),
      getConsultationFullView(caseId).catch(() => null),
    ])
      .then(([base, fullView]) => {
        setRecord(base);
        setFull(fullView);
      })
      .catch((err) => {
        console.error("[report] load failed", err);
        setError(err?.response?.status === 401 ? "Please sign in." : "Could not load report.");
      })
      .finally(() => setLoading(false));
  }, [caseId]);

  const doPrint = () => window.print();

  const doDownload = () => {
    // window.print() lets the user save-as-PDF from the print dialog. Fire it
    // after ensuring the printable region is populated.
    window.print();
  };

  if (loading) {
    return <main className="dyntra-container py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#4B2AAD]" /></main>;
  }
  if (error) {
    return (
      <main className="dyntra-container py-24 text-center">
        <p className="text-sm text-gray-700">{error}</p>
        <Link to="/ai-consultant" className="text-[#4B2AAD] font-semibold hover:underline">Back to dashboard</Link>
      </main>
    );
  }

  const c = full || record || {};
  const report = c.clinical_report || c.ai_report || c.analysis?.report || c.report;
  const summary = c.summary || c.diagnosis || c.assessment;
  const differential = c.differential_diagnoses || c.differentialDiagnoses;
  const nextSteps = c.next_steps || c.nextSteps || c.recommendations;
  const risk = (c.risk_level || c.riskLevel || c.risk || "").toLowerCase();

  return (
    <>
      {/* Print-only styles: hide chrome, print the report body full width */}
      <style>{`
        @media print {
          nav, header, footer, .no-print, [data-nav], [data-footer] { display: none !important; }
          body { background: white !important; }
          .print-region { padding: 0 !important; }
        }
      `}</style>

      <main className="dyntra-container py-8 print-region">
        <div className="flex items-center justify-between gap-3 flex-wrap no-print">
          <Link
            to={`/ai-consultant/${caseId}`}
            data-testid="report-back"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#4B2AAD] hover:underline"
          >
            <ArrowLeft className="h-4 w-4" /> Back to case
          </Link>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="report-print"
              onClick={doPrint}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              <Printer className="h-4 w-4" /> Print
            </button>
            <button
              type="button"
              data-testid="report-download"
              onClick={doDownload}
              className="dyntra-btn-primary px-4 py-2 text-sm"
            >
              <Download className="h-4 w-4" /> Download PDF
            </button>
          </div>
        </div>

        <article ref={printRef} className="mt-6 rounded-2xl border border-gray-100 bg-white shadow-sm p-6 sm:p-10">
          <header className="flex items-start gap-4 flex-wrap justify-between">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-xl bg-[#F3EFFC] flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-[#4B2AAD]" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-[#4B2AAD]">Clinical Report · Case #{c.case_id || c.caseId || c.id}</p>
                <h1 className="font-display text-3xl font-bold text-gray-900">
                  {c.patient_name || c.patientName || c.full_name || c.fullName || "Patient"}
                </h1>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5" /> {c.age || "?"} yrs · {c.gender || "-"}</span>
                  <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {(c.created_at || c.createdAt) ? new Date(c.created_at || c.createdAt).toLocaleDateString() : "—"}</span>
                  <span className="inline-flex items-center gap-1"><Stethoscope className="h-3.5 w-3.5" /> {c.emergency_level || c.emergencyLevel || "Normal"}</span>
                </div>
              </div>
            </div>
            <RiskLevelBadge risk={risk} />
          </header>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <Fact label="Chief complaint" value={c.chief_complaint || c.chiefComplaint} />
            <Fact label="Blood pressure" value={c.blood_pressure || c.bloodPressure} />
            <Fact label="Blood group" value={c.blood_group || c.bloodGroup} />
          </div>

          <Section title="Summary" icon={ClipboardList}>
            {summary ? (
              <p className="text-sm text-gray-800 whitespace-pre-line">{typeof summary === "string" ? summary : JSON.stringify(summary, null, 2)}</p>
            ) : (
              <p className="text-sm text-gray-500">Awaiting AI summary…</p>
            )}
          </Section>

          <Section title="Symptoms" icon={AlertTriangle}>
            <List items={c.symptoms} />
          </Section>

          <Section title="Medical history">
            <Multiline text={c.medical_history || c.medicalHistory} />
          </Section>

          <Section title="Dental history">
            <Multiline text={c.dental_history || c.dentalHistory} />
          </Section>

          <Section title="Doctor's notes">
            <Multiline text={c.doctor_notes || c.doctorNotes} />
          </Section>

          <Section title="Differential diagnoses">
            {differential ? (
              <List items={differential} />
            ) : (
              <p className="text-sm text-gray-500">No differential diagnoses available.</p>
            )}
          </Section>

          <Section title="Clinical report">
            {report ? (
              <div className="prose prose-sm max-w-none whitespace-pre-line text-gray-900">
                {typeof report === "string" ? report : JSON.stringify(report, null, 2)}
              </div>
            ) : (
              <p className="text-sm text-gray-500">Report is being generated.</p>
            )}
          </Section>

          <Section title="Recommended next steps">
            {Array.isArray(nextSteps) && nextSteps.length ? (
              <ol className="list-decimal ml-5 space-y-1 text-sm text-gray-800">
                {nextSteps.map((s, i) => <li key={i}>{typeof s === "string" ? s : s.step || s.title || JSON.stringify(s)}</li>)}
              </ol>
            ) : nextSteps ? (
              <p className="text-sm text-gray-800 whitespace-pre-line">{String(nextSteps)}</p>
            ) : (
              <p className="text-sm text-gray-500">No workflow suggestions.</p>
            )}
          </Section>

          <footer className="mt-10 pt-6 border-t border-gray-100 text-xs text-gray-500">
            Generated by Dr. Dyntra AI. AI recommendations are provided to support — not replace — professional clinical judgement.
          </footer>
        </article>
      </main>
    </>
  );
}

function Fact({ label, value }) {
  return (
    <div className="rounded-xl bg-[#F8F9FA] p-3">
      <p className="text-xs font-bold uppercase tracking-wider text-gray-500">{label}</p>
      <p className="text-sm text-gray-900 mt-0.5">{value || "-"}</p>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section className="mt-6">
      <h2 className="inline-flex items-center gap-2 font-display text-base font-bold text-gray-900">
        {Icon && <Icon className="h-4 w-4 text-[#4B2AAD]" />} {title}
      </h2>
      <div className="mt-2">{children}</div>
    </section>
  );
}

function List({ items }) {
  if (!items) return <p className="text-sm text-gray-500">Not recorded.</p>;
  const arr = Array.isArray(items) ? items : String(items).split(/[\n,]/);
  const trimmed = arr.map((x) => (typeof x === "string" ? x.trim() : x)).filter(Boolean);
  if (!trimmed.length) return <p className="text-sm text-gray-500">Not recorded.</p>;
  return (
    <ul className="list-disc ml-5 space-y-1 text-sm text-gray-800">
      {trimmed.map((x, i) => <li key={i}>{typeof x === "string" ? x : JSON.stringify(x)}</li>)}
    </ul>
  );
}

function Multiline({ text }) {
  if (!text) return <p className="text-sm text-gray-500">Not recorded.</p>;
  return <p className="text-sm text-gray-800 whitespace-pre-line">{String(text)}</p>;
}

function RiskLevelBadge({ risk }) {
  const map = {
    high: { bg: "bg-rose-100", fg: "text-rose-700", label: "High risk" },
    moderate: { bg: "bg-amber-100", fg: "text-amber-700", label: "Moderate risk" },
    low: { bg: "bg-emerald-100", fg: "text-emerald-700", label: "Low risk" },
  };
  const m = map[risk];
  if (!m) return null;
  return (
    <span className={`inline-flex items-center rounded-full ${m.bg} ${m.fg} text-xs font-bold px-3 py-1`}>
      {m.label}
    </span>
  );
}
